/* Tabbed admin Lead Drawer — Overview · Activity · Reminders · Files.
 * Extracted from AdminDashboard.jsx (Phase B) to keep the page manageable.
 * Auth, snooze, status/priority, notes — all preserved unchanged. */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "@/lib/site";
import {
  AlertTriangle, Moon, X, Phone, MessageSquare, Mail, Clock, FileDown,
  CalendarClock, CheckCircle, XCircle, Trash2, Plus, ExternalLink, Archive, RotateCcw,
  Upload, ShieldAlert, Lock, FileText, Eye,
} from "lucide-react";
import {
  OFFER_LABELS, STATUSES, PRIORITIES, ACTIVITY_TYPES, statusMeta, priorityMeta, activityMeta, normalizeStatus,
  isStale, isSnoozed, formatSnoozeUntil, lastTouchedBadge, digits, formatDate,
  nextPendingActivity, formatActivityWhen, isActivityOverdue, isActivityDueToday,
  todayISO,
} from "./leadHelpers";
import { QUICK_TEMPLATES, buildMailtoUrl, buildSmsUrl } from "@/lib/templates";

const TABS = [
  { v: "overview", label: "Overview" },
  { v: "onboarding", label: "Onboarding" },
  { v: "activity", label: "Activity" },
  { v: "reminders", label: "Reminders" },
  { v: "files", label: "Files" },
];

const TIMELINE_TYPE_LABELS = {
  lead_created: "Lead submitted",
  status_changed: "Status changed",
  priority_changed: "Priority changed",
  notes_updated: "Notes updated",
  call_clicked: "Called",
  text_clicked: "Texted",
  email_clicked: "Emailed",
  marked_contacted: "Marked as contacted",
  touched: "Last touched updated",
  snoozed: "Snoozed",
  unsnoozed: "Snooze cancelled",
  scheduled_activity_created: "Scheduled activity created",
  scheduled_activity_updated: "Scheduled activity updated",
  scheduled_activity_completed: "Scheduled activity completed",
  scheduled_activity_canceled: "Scheduled activity canceled",
  scheduled_activity_deleted: "Scheduled activity removed",
  value_updated: "Lead value updated",
  onboarding_updated: "Onboarding item updated",
  admin_file_uploaded: "Internal file uploaded",
  admin_file_removed: "Internal file removed",
  lead_manually_created: "Lead added manually",
  test_lead_marked: "Marked as test lead",
  test_lead_unmarked: "Unmarked as test lead",
};

export default function LeadDrawer({
  lead, allLeads, view = "active", onClose, onUpdate, onDownload, onTouch, onMarkContacted,
  onSnooze, onUnsnooze, onOpenLead, onLeadPatched,
  onArchive, onUnarchive, onTrash, onRestore, onPermanentDelete,
  onToggleTestLead,
  mode = "drawer", // "drawer" (modal slide-in) | "page" (inline, full-width)
}) {
  const [tab, setTab] = useState("overview");
  const [notes, setNotes] = useState(lead.notes || "");
  useEffect(() => { setNotes(lead.notes || ""); }, [lead.notes]);
  // Re-mount-friendly when admin switches between leads via the duplicate panel.
  useEffect(() => { setTab("overview"); }, [lead.id]);

  const sm = statusMeta(lead.status);
  const pm = priorityMeta(lead.priority);
  const stale = isStale(lead);
  const snoozed = isSnoozed(lead);
  const touched = lastTouchedBadge(lead);

  // Map of duplicate id → lead summary for the warning panel.
  const dupLeads = useMemo(() => {
    const ids = lead.possible_duplicate_of || [];
    if (!ids.length || !allLeads) return [];
    const byId = new Map(allLeads.map((l) => [l.id, l]));
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }, [lead.possible_duplicate_of, allLeads]);

  const Body = (
    <>
      <DrawerHeader
        lead={lead} sm={sm} pm={pm} stale={stale} snoozed={snoozed} touched={touched}
        view={view}
        onClose={onClose} onUpdate={onUpdate} onTouch={onTouch}
        onMarkContacted={onMarkContacted} onSnooze={onSnooze} onUnsnooze={onUnsnooze}
        onArchive={onArchive} onUnarchive={onUnarchive} onTrash={onTrash}
        onRestore={onRestore} onPermanentDelete={onPermanentDelete}
        onToggleTestLead={onToggleTestLead}
        mode={mode}
      />

      {/* Tab bar */}
      <div className="px-6 border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button key={t.v} type="button" onClick={() => setTab(t.v)} data-testid={`drawer-tab-${t.v}`}
              className={`px-3 py-2.5 text-[13px] font-semibold transition-colors border-b-2 -mb-px ${
                tab === t.v
                  ? "text-[var(--swipe-accent)] border-[var(--swipe-accent)]"
                  : "text-slate-500 hover:text-slate-700 border-transparent"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" ? (
        <OverviewTab
          lead={lead} dupLeads={dupLeads} notes={notes} setNotes={setNotes}
          onUpdate={onUpdate} onOpenLead={onOpenLead} onLeadPatched={onLeadPatched}
        />
      ) : null}
      {tab === "onboarding" ? <OnboardingTab lead={lead} onLeadPatched={onLeadPatched} /> : null}
      {tab === "activity" ? <ActivityTab lead={lead} /> : null}
      {tab === "reminders" ? <RemindersTab lead={lead} onLeadPatched={onLeadPatched} /> : null}
      {tab === "files" ? <FilesTab lead={lead} onDownload={onDownload} onLeadPatched={onLeadPatched} /> : null}
    </>
  );

  if (mode === "page") {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="lead-detail-page-body">
        {Body}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end" onClick={onClose} data-testid="lead-drawer">
      <aside className="w-full sm:w-[60vw] sm:max-w-[920px] sm:min-w-[680px] bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {Body}
      </aside>
    </div>
  );
}

/* ────────────────────────── Header ────────────────────────── */
function DrawerHeader({ lead, sm, pm, stale, snoozed, touched, view, onClose, onUpdate, onTouch, onMarkContacted, onSnooze, onUnsnooze, onArchive, onUnarchive, onTrash, onRestore, onPermanentDelete, onToggleTestLead, mode = "drawer" }) {
  const isArchived = !!lead.archived;
  const isTrashed = !!lead.trashed;
  return (
    <div className="p-6 border-b border-slate-200">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--swipe-accent)] font-bold">
              {OFFER_LABELS[lead.offer_type] || lead.offer_type}
            </div>
            {stale ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-rose-700 bg-rose-100">
                <AlertTriangle className="w-3 h-3" /> Stale Lead
              </span>
            ) : null}
            {snoozed ? (
              <span data-testid="drawer-snoozed-badge"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-indigo-700 bg-indigo-100">
                <Moon className="w-3 h-3" /> Snoozed until {formatSnoozeUntil(lead.snooze_until)}
              </span>
            ) : null}
            {(lead.possible_duplicate_of || []).length ? (
              <span data-testid="drawer-dup-badge"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-amber-700 bg-amber-100">
                <AlertTriangle className="w-3 h-3" /> Possible Duplicate
              </span>
            ) : null}
            {lead.is_test_lead ? (
              <span data-testid="drawer-test-badge"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-fuchsia-700 bg-fuchsia-100">
                TEST LEAD
              </span>
            ) : null}
            {lead.manually_created ? (
              <span data-testid="drawer-manual-badge"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-[var(--swipe-accent)] bg-[var(--swipe-accent-soft)]">
                Manually Added
              </span>
            ) : null}
            {isArchived ? (
              <span data-testid="drawer-archived-badge"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-amber-800 bg-amber-100">
                <Archive className="w-3 h-3" /> Archived
              </span>
            ) : null}
            {isTrashed ? (
              <span data-testid="drawer-trashed-badge"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-rose-700 bg-rose-100">
                <Trash2 className="w-3 h-3" /> In Trash
              </span>
            ) : null}
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${touched.cls}`}>{touched.label}</span>
          </div>
          <h2 className="text-xl font-extrabold mt-1 text-[var(--swipe-navy)] truncate">{lead.business_name || lead.contact_name}</h2>
          <div className="text-[12px] text-slate-500 mt-1">{formatDate(lead.created_at)}</div>
        </div>
        {mode === "page" ? null : (
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100" data-testid="drawer-close"><X className="w-5 h-5" /></button>
        )}
      </div>

      {/* Quick action buttons */}
      <div className="mt-4 flex flex-wrap gap-2">
        {lead.phone ? (
          <>
            <a href={`tel:${digits(lead.phone)}`} onClick={() => onTouch("call")} data-testid="drawer-call"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
              <Phone className="w-4 h-4" /> Call
            </a>
            <a href={`sms:${digits(lead.phone)}`} onClick={() => onTouch("text")} data-testid="drawer-text"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
              <MessageSquare className="w-4 h-4" /> Text
            </a>
          </>
        ) : null}
        {lead.email ? (
          <a href={`mailto:${lead.email}`} onClick={() => onTouch("email")} data-testid="drawer-email"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-sm font-semibold">
            <Mail className="w-4 h-4" /> Email
          </a>
        ) : null}
        <button type="button" onClick={onMarkContacted} data-testid="drawer-mark-contacted"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)] text-slate-700 text-sm font-semibold">
          Mark as Contacted
        </button>
        {snoozed ? (
          <button type="button" onClick={onUnsnooze} data-testid="drawer-unsnooze"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm font-semibold">
            <Moon className="w-4 h-4" /> Cancel Snooze
          </button>
        ) : (
          <button type="button" onClick={onSnooze} data-testid="drawer-snooze"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 text-slate-700 text-sm font-semibold">
            <Clock className="w-4 h-4" /> Snooze 24h
          </button>
        )}
        {/* Archive / Trash / Restore / Permanent Delete — shape depends on the lead's current state. */}
        {!isArchived && !isTrashed ? (
          <>
            <button type="button" onClick={onArchive} data-testid="drawer-archive"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800 text-slate-700 text-sm font-semibold">
              <Archive className="w-4 h-4" /> Archive
            </button>
            <button type="button" onClick={onTrash} data-testid="drawer-trash"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 text-slate-700 text-sm font-semibold">
              <Trash2 className="w-4 h-4" /> Move to Trash
            </button>
          </>
        ) : null}
        {isArchived && !isTrashed ? (
          <button type="button" onClick={onUnarchive} data-testid="drawer-unarchive"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 text-sm font-semibold">
            <RotateCcw className="w-4 h-4" /> Unarchive
          </button>
        ) : null}
        {isTrashed ? (
          <>
            <button type="button" onClick={onRestore} data-testid="drawer-restore"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 text-slate-700 text-sm font-semibold">
              <RotateCcw className="w-4 h-4" /> Restore from Trash
            </button>
            <button type="button" onClick={onPermanentDelete} data-testid="drawer-permanent-delete"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold">
              <Trash2 className="w-4 h-4" /> Delete Permanently
            </button>
          </>
        ) : null}
        {onToggleTestLead ? (
          <button
            type="button"
            onClick={() => onToggleTestLead(!lead.is_test_lead)}
            data-testid="drawer-toggle-test-lead"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-semibold ${
              lead.is_test_lead
                ? "bg-fuchsia-100 border-fuchsia-200 text-fuchsia-800 hover:bg-fuchsia-200"
                : "border-slate-200 text-slate-700 hover:border-fuchsia-300 hover:bg-fuchsia-50 hover:text-fuchsia-700"
            }`}
            title={lead.is_test_lead ? "Unmark as test lead" : "Mark as test lead (excluded from metrics)"}
          >
            {lead.is_test_lead ? "Unmark Test Lead" : "Mark as Test Lead"}
          </button>
        ) : null}
      </div>

      {/* Priority + Status pickers */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Priority</div>
          <select value={lead.priority || "warm"} onChange={(e) => onUpdate(lead.id, { priority: e.target.value })}
            data-testid="drawer-priority"
            className={`text-[12px] font-semibold w-full px-2 py-1.5 rounded-lg border ${pm.color} outline-none cursor-pointer`}>
            {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Status</div>
          <select value={normalizeStatus(lead.status)} onChange={(e) => onUpdate(lead.id, { status: e.target.value })}
            data-testid="drawer-status"
            className={`text-[12px] font-semibold w-full px-2 py-1.5 rounded-lg border ${sm.color} outline-none cursor-pointer`}>
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────── Overview tab ────────────────────────── */
function OverviewTab({ lead, dupLeads, notes, setNotes, onUpdate, onOpenLead, onLeadPatched }) {
  const next = nextPendingActivity(lead);
  const overdue = next ? isActivityOverdue(next) : false;

  return (
    <div className="p-6 space-y-5 text-sm">
      {dupLeads.length ? (
        <DuplicatePanel dupLeads={dupLeads} onOpenLead={onOpenLead} />
      ) : null}

      {next ? (
        <div className={`rounded-lg border px-3 py-2.5 text-[13px] flex items-center justify-between gap-3 ${
          overdue ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-[var(--swipe-soft-blue)] border-[var(--swipe-accent)]/20 text-[var(--swipe-navy)]"
        }`} data-testid="drawer-next-activity">
          <div className="flex items-center gap-2 min-w-0">
            <CalendarClock className={`w-4 h-4 shrink-0 ${overdue ? "text-rose-600" : "text-[var(--swipe-accent)]"}`} />
            <div className="min-w-0">
              <div className="font-semibold truncate">
                {overdue ? "Overdue: " : "Next: "}{activityMeta(next.type).label} · {formatActivityWhen(next)}
              </div>
              {next.note ? <div className="text-[11.5px] opacity-80 truncate">{next.note}</div> : null}
            </div>
          </div>
        </div>
      ) : null}

      <Row k="Business name" v={lead.business_name} />
      <Row k="Contact name" v={lead.contact_name} />
      <Row k="Phone" v={lead.phone ? <a href={`tel:${lead.phone}`} className="text-[var(--swipe-accent)]">{lead.phone}</a> : null} />
      <Row k="Email" v={lead.email ? <a href={`mailto:${lead.email}`} className="text-[var(--swipe-accent)]">{lead.email}</a> : null} />
      <Row k="Lead type" v={OFFER_LABELS[lead.offer_type] || lead.offer_type} />
      <Row k="Source" v={lead.source_label || "Direct / Unknown"} />
      <Row k="Source page" v={lead.source_page} />
      <Row k="Landing page" v={lead.landing_page_url} />
      <Row k="Submitted" v={formatDate(lead.created_at)} />
      <Row k="Last touched" v={lead.last_touched_at ? formatDate(lead.last_touched_at) : "Never"} />
      <Row k="Business type" v={lead.business_type} />
      <Row k="Monthly volume" v={lead.monthly_volume} />
      <Row k="Current processor" v={lead.current_processor} />
      <Row k="Current POS" v={lead.current_pos} />
      <Row k="Cash discount interest" v={lead.cash_discount_interest} />
      <Row k="Free POS interest" v={lead.free_pos_interest} />
      <Row k="Interests" v={Array.isArray(lead.interests) ? lead.interests.join(", ") : lead.interests} />
      <Row k="Timeframe" v={lead.timeframe} />
      <Row k="Message" v={lead.message} multiline />

      {lead.answers && Object.keys(lead.answers).length ? (
        <div className="pt-2">
          <SectionTitle>Questionnaire answers</SectionTitle>
          {Object.entries(lead.answers).map(([q, a]) => (
            <div key={q} className="mb-2">
              <div className="text-[12px] text-slate-500">{q}</div>
              <div className="text-[14px] font-semibold text-[var(--swipe-navy)]">{a}</div>
            </div>
          ))}
        </div>
      ) : null}

      <LeadValueEditor lead={lead} onLeadPatched={onLeadPatched} />

      <QuickTemplates lead={lead} />

      <UtmDetails lead={lead} />

      <div className="pt-4 border-t border-slate-200">
        <SectionTitle>Internal notes</SectionTitle>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
          data-testid="lead-notes"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[var(--swipe-accent)] focus:ring-2 focus:ring-[var(--swipe-accent)]/15 outline-none text-sm"
          placeholder="Add notes about this lead…" />
        <button onClick={() => onUpdate(lead.id, { notes })}
          data-testid="lead-notes-save"
          className="mt-2 px-3 py-1.5 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-sm font-semibold">
          Save notes
        </button>
      </div>
    </div>
  );
}

function DuplicatePanel({ dupLeads, onOpenLead }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="drawer-dup-panel">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-700" />
        <div className="text-[12.5px] font-bold text-amber-800 uppercase tracking-wider">
          Possible Duplicate{dupLeads.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="space-y-2">
        {dupLeads.map((d) => (
          <div key={d.id} className="bg-white rounded border border-amber-200 px-3 py-2 flex items-center justify-between gap-3 text-[12.5px]">
            <div className="min-w-0">
              <div className="font-semibold text-[var(--swipe-navy)] truncate">{d.business_name || d.contact_name || "—"}</div>
              <div className="text-slate-500 truncate">
                {d.contact_name || "—"} · {d.phone || "—"} · {d.email || "—"}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                {OFFER_LABELS[d.offer_type] || d.offer_type} · {formatDate(d.created_at)} · {statusMeta(d.status).label}
              </div>
            </div>
            <button type="button" onClick={() => onOpenLead && onOpenLead(d)}
              data-testid={`drawer-open-dup-${d.id}`}
              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-100 text-[11.5px] font-semibold">
              <ExternalLink className="w-3 h-3" /> Open
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LeadValueEditor({ lead, onLeadPatched }) {
  const [vol, setVol] = useState(lead.monthly_volume_amount ?? "");
  const [save, setSave] = useState(lead.est_monthly_savings ?? "");
  const [profit, setProfit] = useState(lead.est_monthly_profit ?? "");
  const [annual, setAnnual] = useState(lead.est_annual_value ?? "");
  useEffect(() => {
    setVol(lead.monthly_volume_amount ?? "");
    setSave(lead.est_monthly_savings ?? "");
    setProfit(lead.est_monthly_profit ?? "");
    setAnnual(lead.est_annual_value ?? "");
  }, [lead.id, lead.monthly_volume_amount, lead.est_monthly_savings, lead.est_monthly_profit, lead.est_annual_value]);

  const submit = async () => {
    const payload = {};
    const num = (v) => v === "" || v === null || v === undefined ? null : Number(v);
    if (num(vol) !== null) payload.monthly_volume_amount = num(vol);
    if (num(save) !== null) payload.est_monthly_savings = num(save);
    if (num(profit) !== null) payload.est_monthly_profit = num(profit);
    if (num(annual) !== null) payload.est_annual_value = num(annual);
    try {
      await axios.patch(`${API_BASE}/admin/leads/${lead.id}/value`, payload, { withCredentials: true });
      onLeadPatched && onLeadPatched(lead.id, payload);
      toast.success("Lead value saved");
    } catch {
      toast.error("Unable to save lead value");
    }
  };

  return (
    <div className="pt-4 border-t border-slate-200">
      <SectionTitle>Lead value</SectionTitle>
      <div className="grid grid-cols-2 gap-2.5">
        <ValueInput label="Monthly volume ($)" v={vol} onChange={setVol} testId="value-monthly-volume" />
        <ValueInput label="Est. monthly savings ($)" v={save} onChange={setSave} testId="value-monthly-savings" />
        <ValueInput label="Est. monthly profit ($)" v={profit} onChange={setProfit} testId="value-monthly-profit" />
        <ValueInput label="Est. annual value ($)" v={annual} onChange={setAnnual} testId="value-annual" />
      </div>
      <button onClick={submit} data-testid="value-save"
        className="mt-2 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)] text-slate-700 text-sm font-semibold">
        Save value
      </button>
    </div>
  );
}

function ValueInput({ label, v, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-slate-500 mb-1">{label}</span>
      <input type="number" step="0.01" inputMode="decimal" value={v} onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 focus:border-[var(--swipe-accent)] focus:ring-2 focus:ring-[var(--swipe-accent)]/15 outline-none text-sm" />
    </label>
  );
}

function QuickTemplates({ lead }) {
  return (
    <div className="pt-4 border-t border-slate-200">
      <SectionTitle>Quick messages</SectionTitle>
      <p className="text-[11.5px] text-slate-500 mb-2">Opens a draft — review and edit before sending.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {QUICK_TEMPLATES.map((t) => (
          <div key={t.id} className="flex items-stretch border border-slate-200 rounded-lg overflow-hidden text-[12px]">
            <div className="flex-1 px-2.5 py-2 text-slate-700 font-semibold leading-tight">{t.label}</div>
            <div className="flex">
              {lead.email ? (
                <a href={buildMailtoUrl(t, lead)} title="Open email draft"
                  data-testid={`template-email-${t.id}`}
                  className="px-2 inline-flex items-center justify-center border-l border-slate-200 text-slate-600 hover:bg-[var(--swipe-soft-blue)] hover:text-[var(--swipe-accent)]">
                  <Mail className="w-3.5 h-3.5" />
                </a>
              ) : null}
              {lead.phone ? (
                <a href={buildSmsUrl(t, lead)} title="Open SMS draft"
                  data-testid={`template-sms-${t.id}`}
                  className="px-2 inline-flex items-center justify-center border-l border-slate-200 text-slate-600 hover:bg-blue-50 hover:text-blue-700">
                  <MessageSquare className="w-3.5 h-3.5" />
                </a>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UtmDetails({ lead }) {
  const [open, setOpen] = useState(false);
  const fields = [
    ["utm_source", lead.utm_source],
    ["utm_medium", lead.utm_medium],
    ["utm_campaign", lead.utm_campaign],
    ["utm_content", lead.utm_content],
    ["utm_term", lead.utm_term],
    ["fbclid", lead.fbclid ? "present" : null],
    ["gclid", lead.gclid ? "present" : null],
    ["First-touch source", lead.first_touch_source],
    ["Last-touch source", lead.last_touch_source],
    ["Landing page URL", lead.landing_page_url],
    ["Referrer URL", lead.referrer_url],
  ].filter(([, v]) => v);

  return (
    <div className="pt-4 border-t border-slate-200">
      <button type="button" onClick={() => setOpen((o) => !o)}
        data-testid="drawer-utm-toggle"
        className="text-[11px] uppercase tracking-wider text-slate-500 font-bold inline-flex items-center gap-1 hover:text-slate-700">
        Tracking details {open ? "▾" : "▸"} <span className="text-slate-400">({fields.length})</span>
      </button>
      {open ? (
        <div className="mt-2 space-y-1.5 text-[12px]">
          {fields.length ? fields.map(([k, v]) => (
            <div key={k} className="flex gap-3">
              <div className="w-40 shrink-0 text-slate-500 uppercase text-[10.5px] tracking-wider font-semibold">{k}</div>
              <div className="text-[var(--swipe-navy)] break-all">{v}</div>
            </div>
          )) : <div className="text-slate-400">No tracking captured (Direct / Unknown).</div>}
        </div>
      ) : null}
    </div>
  );
}

/* ────────────────────────── Activity tab ────────────────────────── */
function ActivityTab({ lead }) {
  const events = useMemo(() => {
    const arr = [...(lead.activity_timeline || [])];
    arr.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    return arr;
  }, [lead.activity_timeline]);

  if (!events.length) return (
    <div className="p-6 text-center text-slate-400 text-sm">No activity recorded yet.</div>
  );

  return (
    <div className="p-6 space-y-3 text-sm" data-testid="drawer-activity">
      {events.map((e, i) => (
        <div key={e.id || i} className="flex gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] mt-2 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[var(--swipe-navy)]">
              {TIMELINE_TYPE_LABELS[e.type] || e.type}
            </div>
            <div className="text-[11.5px] text-slate-500">
              {formatDate(e.at)}{e.by ? ` · ${e.by}` : ""}
            </div>
            {e.meta && Object.keys(e.meta).length ? (
              <div className="text-[12px] text-slate-600 mt-0.5 break-words">
                {renderMeta(e.type, e.meta)}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function renderMeta(type, meta) {
  if (type === "status_changed" || type === "priority_changed") {
    return `${meta.from || "—"} → ${meta.to || "—"}`;
  }
  if (type === "snoozed") {
    return `Until ${formatDate(meta.snooze_until)}`;
  }
  if (type === "scheduled_activity_created" || type === "scheduled_activity_updated"
    || type === "scheduled_activity_completed" || type === "scheduled_activity_canceled") {
    const parts = [];
    if (meta.type) parts.push(activityMeta(meta.type).label);
    if (meta.date) parts.push(meta.date);
    if (meta.time) parts.push(meta.time);
    if (meta.status) parts.push(`(${meta.status})`);
    return parts.join(" · ");
  }
  if (type === "value_updated") {
    const dollars = (n) => Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
    return Object.entries(meta).map(([k, v]) => `${k}: ${dollars(v)}`).join(" · ");
  }
  return Object.entries(meta).map(([k, v]) => `${k}: ${v}`).join(" · ");
}

/* ────────────────────────── Reminders tab ────────────────────────── */
function RemindersTab({ lead, onLeadPatched }) {
  const [acts, setActs] = useState(lead.scheduled_activities || []);
  useEffect(() => { setActs(lead.scheduled_activities || []); }, [lead.scheduled_activities]);

  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState("call");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("10:00");
  const [note, setNote] = useState("");

  const sorted = useMemo(() => {
    return [...acts].sort((a, b) => {
      const da = a.date ? new Date(`${a.date}T${a.time || "00:00"}:00`).getTime() : Infinity;
      const dbb = b.date ? new Date(`${b.date}T${b.time || "00:00"}:00`).getTime() : Infinity;
      return da - dbb;
    });
  }, [acts]);

  const create = async () => {
    try {
      const r = await axios.post(`${API_BASE}/admin/leads/${lead.id}/scheduled-activity`,
        { type, date, time, note }, { withCredentials: true });
      const next = [...acts, r.data.activity];
      setActs(next);
      onLeadPatched && onLeadPatched(lead.id, { scheduled_activities: next });
      setShowForm(false);
      setNote("");
      toast.success("Scheduled");
    } catch {
      toast.error("Unable to schedule");
    }
  };

  const patchAct = async (id, patch) => {
    try {
      const r = await axios.patch(`${API_BASE}/admin/leads/${lead.id}/scheduled-activity/${id}`,
        patch, { withCredentials: true });
      const next = acts.map((a) => (a.id === id ? r.data.activity : a));
      setActs(next);
      onLeadPatched && onLeadPatched(lead.id, { scheduled_activities: next });
    } catch {
      toast.error("Unable to update");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API_BASE}/admin/leads/${lead.id}/scheduled-activity/${id}`, { withCredentials: true });
      const next = acts.filter((a) => a.id !== id);
      setActs(next);
      onLeadPatched && onLeadPatched(lead.id, { scheduled_activities: next });
      toast.success("Removed");
    } catch {
      toast.error("Unable to remove");
    }
  };

  return (
    <div className="p-6 text-sm" data-testid="drawer-reminders">
      <div className="flex items-center justify-between mb-3">
        <SectionTitle>Scheduled activities</SectionTitle>
        <button type="button" onClick={() => setShowForm((v) => !v)}
          data-testid="reminders-add-toggle"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-[12px] font-semibold">
          <Plus className="w-3.5 h-3.5" /> {showForm ? "Cancel" : "Add"}
        </button>
      </div>

      {showForm ? (
        <div className="rounded-lg border border-slate-200 p-3 mb-4 bg-slate-50">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[11px] text-slate-500 mb-1">Type</span>
              <select value={type} onChange={(e) => setType(e.target.value)} data-testid="reminders-form-type"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm bg-white">
                {ACTIVITY_TYPES.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-[11px] text-slate-500 mb-1">Date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                data-testid="reminders-form-date"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm bg-white" />
            </label>
            <label className="block">
              <span className="block text-[11px] text-slate-500 mb-1">Time</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                data-testid="reminders-form-time"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm bg-white" />
            </label>
            <label className="block col-span-2">
              <span className="block text-[11px] text-slate-500 mb-1">Note (optional)</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. ask for statement"
                data-testid="reminders-form-note"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm bg-white" />
            </label>
          </div>
          <button type="button" onClick={create} data-testid="reminders-form-save"
            className="mt-2 px-3 py-1.5 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-sm font-semibold">
            Save activity
          </button>
        </div>
      ) : null}

      {sorted.length ? (
        <div className="space-y-2">
          {sorted.map((a) => {
            const overdue = isActivityOverdue(a);
            const due = isActivityDueToday(a);
            const meta = activityMeta(a.type);
            return (
              <div key={a.id} data-testid={`reminders-item-${a.id}`}
                className={`rounded-lg border p-3 ${
                  a.status === "completed" ? "bg-emerald-50/50 border-emerald-200" :
                  a.status === "canceled" ? "bg-slate-50 border-slate-200 opacity-70" :
                  overdue ? "bg-rose-50 border-rose-200" :
                  due ? "bg-[var(--swipe-soft-blue)] border-[var(--swipe-accent)]/30" :
                  "bg-white border-slate-200"
                }`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-[var(--swipe-navy)]">
                      {meta.label} <span className="text-slate-500 font-normal">· {formatActivityWhen(a)}</span>
                    </div>
                    {a.note ? <div className="text-[12px] text-slate-600 mt-0.5">{a.note}</div> : null}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <StatusPill status={a.status} overdue={overdue} due={due} />
                      {a.created_by ? (
                        <span className="text-[10.5px] text-slate-400">by {a.created_by}</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {a.status !== "completed" ? (
                      <button type="button" onClick={() => patchAct(a.id, { status: "completed" })}
                        title="Mark completed" data-testid={`reminders-complete-${a.id}`}
                        className="w-7 h-7 rounded-lg border border-slate-200 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700 inline-flex items-center justify-center text-slate-600">
                        <CheckCircle className="w-3.5 h-3.5" />
                      </button>
                    ) : null}
                    {a.status === "pending" ? (
                      <button type="button" onClick={() => patchAct(a.id, { status: "canceled" })}
                        title="Cancel" data-testid={`reminders-cancel-${a.id}`}
                        className="w-7 h-7 rounded-lg border border-slate-200 hover:bg-slate-100 inline-flex items-center justify-center text-slate-600">
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    ) : null}
                    <button type="button" onClick={() => remove(a.id)}
                      title="Remove" data-testid={`reminders-remove-${a.id}`}
                      className="w-7 h-7 rounded-lg border border-slate-200 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 inline-flex items-center justify-center text-slate-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center text-slate-400 text-sm py-6">
          No scheduled activities. Click <span className="font-semibold">Add</span> to schedule a follow-up, call, meeting, or text.
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, overdue, due }) {
  let label = status[0].toUpperCase() + status.slice(1);
  let cls = "bg-slate-100 text-slate-600 border-slate-200";
  if (status === "completed") cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (status === "canceled") cls = "bg-slate-100 text-slate-500 border-slate-200";
  else if (overdue) { label = "Overdue"; cls = "bg-rose-50 text-rose-700 border-rose-200"; }
  else if (due) { label = "Due today"; cls = "bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)] border-[var(--swipe-accent)]/30"; }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${cls}`}>{label}</span>;
}

/* ────────────────────────── Onboarding tab ────────────────────────── */
const ONBOARDING_ITEMS = [
  { key: "personal_id", label: "Personal ID", upload: true, required: true },
  { key: "ssn", label: "Social Security", upload: true, required: true, sensitive: true, last4: true },
  { key: "business_tax_id", label: "Business Tax ID / EIN", upload: true, required: true },
  { key: "voided_check", label: "Business Check / Voided Check", upload: true, required: true },
  { key: "personal_phone", label: "Personal Phone Number", upload: false, required: true },
  { key: "business_phone", label: "Business Phone Number", upload: false, required: true },
  { key: "email", label: "Email Address", upload: false, required: true },
  { key: "ebt_license", label: "EBT License (only if applicable)", upload: true, required: false },
];
const ONBOARDING_STATUSES = [
  { v: "needed", label: "Needed" },
  { v: "received", label: "Received" },
  { v: "not_applicable", label: "Not Applicable" },
];

function OnboardingTab({ lead, onLeadPatched }) {
  const [busy, setBusy] = useState(null); // item_key currently saving
  const [uploading, setUploading] = useState(null);
  const info = lead.onboarding_info || {};

  const refreshLead = async () => {
    try {
      const r = await axios.get(`${API_BASE}/admin/leads/${lead.id}`, { withCredentials: true });
      onLeadPatched?.(lead.id, {
        onboarding_info: r.data.onboarding_info,
        admin_files: r.data.admin_files,
        activity_timeline: r.data.activity_timeline,
      });
    } catch (err) { console.warn("[onboarding] refresh failed:", err); }
  };

  const updateItem = async (key, patch) => {
    setBusy(key);
    try {
      await axios.put(
        `${API_BASE}/admin/leads/${lead.id}/onboarding/${key}`,
        patch, { withCredentials: true },
      );
      await refreshLead();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(null);
    }
  };

  const uploadFor = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Match server.py ADMIN_FILE_CATEGORIES — fallback to "other" if no mapping.
      const catMap = {
        personal_id: "personal_id", ssn: "personal_id", business_tax_id: "business_tax_id",
        voided_check: "voided_check", ebt_license: "ebt_license",
      };
      fd.append("category", catMap[key] || "other");
      fd.append("onboarding_item", key);
      await axios.post(
        `${API_BASE}/admin/leads/${lead.id}/admin-files`,
        fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("File uploaded");
      await refreshLead();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  // Progress: required items resolved (received OR not_applicable). EBT counts
  // only if explicitly marked received OR not_applicable.
  const requiredKeys = ONBOARDING_ITEMS.filter((x) => x.required).map((x) => x.key);
  const totalRequired = requiredKeys.length + 1; // include EBT in the visible "X of 8 complete"
  const resolvedCount = ONBOARDING_ITEMS.filter((x) => {
    const s = info[x.key]?.status;
    return s === "received" || s === "not_applicable";
  }).length;
  const missing = ONBOARDING_ITEMS.filter((x) => {
    const s = info[x.key]?.status;
    return x.required && s !== "received" && s !== "not_applicable";
  });

  return (
    <div className="p-6 text-sm" data-testid="drawer-onboarding">
      {/* Progress + missing summary */}
      <div className="rounded-xl border border-[var(--swipe-accent)]/20 bg-[var(--swipe-accent-soft)]/40 p-4 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[var(--swipe-accent)]">
              Onboarding progress
            </div>
            <div className="mt-1 font-display text-[18px] font-extrabold text-[var(--swipe-navy)]">
              {resolvedCount} of {totalRequired} complete
            </div>
          </div>
          <div className="flex-1 max-w-[260px] min-w-[140px]">
            <div className="h-2 rounded-full bg-white border border-[var(--swipe-accent)]/15 overflow-hidden">
              <div
                className="h-full bg-[var(--swipe-accent)] transition-all"
                style={{ width: `${Math.min(100, (resolvedCount / totalRequired) * 100)}%` }}
              />
            </div>
          </div>
        </div>
        {missing.length ? (
          <div className="mt-3 text-[12.5px] text-slate-700" data-testid="onboarding-missing">
            <span className="font-bold text-[var(--swipe-navy)]">Missing:</span>{" "}
            {missing.map((m) => m.label).join(", ")}
          </div>
        ) : (
          <div className="mt-3 text-[12.5px] text-emerald-700 font-semibold">
            All required onboarding items are received or not applicable.
          </div>
        )}
      </div>

      {/* Sensitive-data warning */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-4 flex items-start gap-2.5">
        <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-[12px] text-amber-900 leading-relaxed">
          <span className="font-bold">Sensitive documents are admin-only.</span>{" "}
          Do not type full Social Security numbers into notes. Files here are never
          included in CSV exports or customer emails.
        </div>
      </div>

      {/* Checklist */}
      <ul className="space-y-3">
        {ONBOARDING_ITEMS.map((item) => {
          const cur = info[item.key] || { status: "needed" };
          const matchedFile = (lead.admin_files || []).find((f) => f.id === cur.file_id);
          return (
            <li
              key={item.key}
              data-testid={`onboarding-item-${item.key}`}
              className={`rounded-xl border bg-white p-3.5 ${
                cur.status === "received"
                  ? "border-emerald-200 bg-emerald-50/30"
                  : cur.status === "not_applicable"
                    ? "border-slate-200 bg-slate-50/50"
                    : "border-slate-200"
              }`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5 min-w-0">
                  <input
                    type="checkbox"
                    checked={cur.status === "received"}
                    onChange={(e) =>
                      updateItem(item.key, { status: e.target.checked ? "received" : "needed" })
                    }
                    disabled={busy === item.key}
                    data-testid={`onboarding-checkbox-${item.key}`}
                    className="w-4 h-4 rounded border-slate-300 text-[var(--swipe-accent)] focus:ring-[var(--swipe-accent)]"
                  />
                  <div className="min-w-0">
                    <div className="font-display font-bold text-[14px] text-[var(--swipe-navy)] flex items-center gap-1.5">
                      {item.sensitive ? <Lock className="w-3.5 h-3.5 text-amber-700" /> : null}
                      {item.label}
                      {item.required ? null : (
                        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Optional</span>
                      )}
                    </div>
                    {cur.last_updated_at ? (
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Updated {formatDate(cur.last_updated_at)}{cur.last_updated_by ? ` · ${cur.last_updated_by}` : ""}
                      </div>
                    ) : null}
                  </div>
                </div>
                <select
                  value={cur.status || "needed"}
                  onChange={(e) => updateItem(item.key, { status: e.target.value })}
                  disabled={busy === item.key}
                  data-testid={`onboarding-status-${item.key}`}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-[12.5px] font-semibold bg-white"
                >
                  {ONBOARDING_STATUSES.map((s) => (
                    <option key={s.v} value={s.v}>{s.label}</option>
                  ))}
                </select>
              </div>

              {/* SSN last-4 input (sensitive — never store full SSN) */}
              {item.last4 ? (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Last 4</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    pattern="\d{4}"
                    value={cur.last4 || ""}
                    onChange={(e) => updateItem(item.key, { last4: e.target.value })}
                    placeholder="••••"
                    data-testid={`onboarding-last4-${item.key}`}
                    className="w-20 px-2 py-1 rounded-md border border-slate-200 text-[13px] font-mono tabular-nums tracking-widest"
                  />
                  <span className="text-[11px] text-slate-400">Last 4 digits only — never full SSN.</span>
                </div>
              ) : null}

              <div className="mt-3 grid sm:grid-cols-[1fr,auto] gap-2.5">
                <input
                  type="text"
                  value={cur.note || ""}
                  onChange={(e) => updateItem(item.key, { note: e.target.value })}
                  placeholder="Internal note (no full SSNs)…"
                  data-testid={`onboarding-note-${item.key}`}
                  className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-[13px]"
                />
                {item.upload ? (
                  <div className="flex items-center gap-2">
                    {matchedFile ? (
                      <a
                        href={`${API_BASE}/admin/leads/${lead.id}/admin-files/${matchedFile.id}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`onboarding-view-${item.key}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)] text-[12.5px] font-semibold whitespace-nowrap"
                      >
                        <Eye className="w-3.5 h-3.5" /> View file
                      </a>
                    ) : null}
                    <label
                      data-testid={`onboarding-upload-${item.key}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[var(--swipe-accent)] text-slate-700 hover:text-[var(--swipe-accent)] text-[12.5px] font-semibold cursor-pointer whitespace-nowrap"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploading === item.key ? "Uploading…" : matchedFile ? "Replace" : "Upload"}
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={(e) => { uploadFor(item.key, e.target.files?.[0]); e.target.value = ""; }}
                        disabled={uploading === item.key}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ────────────────────────── Files tab ────────────────────────── */
const ADMIN_FILE_CATEGORIES = [
  { v: "processing_statement", label: "Processing Statement" },
  { v: "personal_id", label: "Personal ID" },
  { v: "business_tax_id", label: "Business Tax ID / EIN" },
  { v: "voided_check", label: "Voided Check / Business Check" },
  { v: "ebt_license", label: "EBT License" },
  { v: "signed_agreement", label: "Signed Agreement" },
  { v: "proposal", label: "Proposal" },
  { v: "pos_quote", label: "POS Quote" },
  { v: "merchant_application", label: "Merchant Application" },
  { v: "other", label: "Other" },
];
const ADMIN_CAT_LABEL = Object.fromEntries(ADMIN_FILE_CATEGORIES.map((c) => [c.v, c.label]));

function FilesTab({ lead, onDownload, onLeadPatched }) {
  const [category, setCategory] = useState("other");
  const [uploading, setUploading] = useState(false);

  const refreshLead = async () => {
    try {
      const r = await axios.get(`${API_BASE}/admin/leads/${lead.id}`, { withCredentials: true });
      onLeadPatched?.(lead.id, {
        onboarding_info: r.data.onboarding_info,
        admin_files: r.data.admin_files,
        activity_timeline: r.data.activity_timeline,
      });
    } catch (err) { console.warn("[files] refresh failed:", err); }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      await axios.post(
        `${API_BASE}/admin/leads/${lead.id}/admin-files`,
        fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("File uploaded");
      await refreshLead();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Remove "${file.original_filename}"?\n\nThis cannot be undone for the merchant copy.`)) return;
    try {
      await axios.delete(
        `${API_BASE}/admin/leads/${lead.id}/admin-files/${file.id}`,
        { withCredentials: true },
      );
      toast.success("File removed");
      await refreshLead();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const adminFiles = lead.admin_files || [];

  return (
    <div className="p-6 text-sm" data-testid="drawer-files">
      {/* Sensitive-data notice */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-4 flex items-start gap-2.5">
        <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-[12px] text-amber-900 leading-relaxed">
          <span className="font-bold">Internal files — admin only.</span>{" "}
          Never included in CSV exports or customer emails. Only visible on this lead&apos;s admin detail page.
        </div>
      </div>

      <SectionTitle>Customer-submitted statement</SectionTitle>
      {lead.statement_path ? (
        <button type="button" onClick={onDownload} data-testid="files-statement-download"
          className="mb-5 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)] font-semibold text-sm">
          <FileDown className="w-4 h-4" /> {lead.statement_filename || "View statement"}
        </button>
      ) : (
        <div className="mb-5 text-slate-400 text-[12.5px]">No statement submitted by the merchant for this lead.</div>
      )}

      {/* Upload bar */}
      <SectionTitle>Upload internal file</SectionTitle>
      <div className="mb-5 flex items-center gap-2 flex-wrap">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="files-upload-category"
          className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-[12.5px] font-semibold"
        >
          {ADMIN_FILE_CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
        </select>
        <label
          data-testid="files-upload-input"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-[12.5px] font-semibold cursor-pointer whitespace-nowrap"
        >
          <Upload className="w-3.5 h-3.5" />
          {uploading ? "Uploading…" : "Upload file"}
          <input
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
            disabled={uploading}
          />
        </label>
        <span className="text-[11px] text-slate-400">PDF, JPG, PNG, WEBP — max 10MB.</span>
      </div>

      {/* List */}
      <SectionTitle>Internal files</SectionTitle>
      {adminFiles.length === 0 ? (
        <div className="text-slate-400 text-[12.5px] py-3" data-testid="files-empty">
          No internal files yet. Use the upload bar above to attach a document.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="files-list">
          {adminFiles.map((f) => (
            <li
              key={f.id}
              data-testid={`files-row-${f.id}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 hover:border-[var(--swipe-accent)]/40 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-[13.5px] text-[var(--swipe-navy)] truncate" title={f.original_filename}>
                    {f.original_filename}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {ADMIN_CAT_LABEL[f.category] || f.category} · {formatDate(f.uploaded_at)}
                    {f.uploaded_by ? ` · ${f.uploaded_by}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <a
                  href={`${API_BASE}/admin/leads/${lead.id}/admin-files/${f.id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`files-view-${f.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)] text-slate-700 text-[12px] font-semibold"
                >
                  <Eye className="w-3.5 h-3.5" /> View
                </a>
                <button
                  type="button"
                  onClick={() => handleDelete(f)}
                  data-testid={`files-delete-${f.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-[12px] font-semibold"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ────────────────────────── Shared bits ────────────────────────── */
function Row({ k, v, multiline }) {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{k}</div>
      <div className={`col-span-2 text-[14px] text-[var(--swipe-navy)] ${multiline ? "whitespace-pre-wrap" : ""}`}>{v}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">{children}</div>;
}
